import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  RunEventType,
  type LogLine,
  type RunEvent,
  type RunSummaryDto,
  type StageDto,
} from '@aiai/shared';
import { getToken } from '../lib/token';
import { refreshAccessToken } from '../lib/api';
import { getActiveOrgId } from '../lib/activeOrg';

export type SocketStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected';

export interface RunSocketHandlers {
  onLog?: (line: LogLine, seq: number) => void;
  onStage?: (stage: StageDto, seq: number) => void;
  onStatus?: (summary: RunSummaryDto, seq: number) => void;
  onAgentEvent?: (payload: Record<string, unknown>, seq: number) => void;
  /**
   * Fired when the socket reconnects after a drop. The consumer should re-fetch
   * the run + replay buffer to backfill any events missed while offline (seq
   * de-dup makes re-applying the buffer safe).
   */
  onReconnect?: () => void;
}

const SOCKET_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Subscribes to the `/runs` socket.io namespace for a single run and routes
 * each `run-event` to the appropriate handler. Handlers are kept in a ref so
 * that changing callbacks doesn't tear down the connection.
 */
export function useRunSocket(
  runId: string | undefined,
  handlers: RunSocketHandlers,
): SocketStatus {
  const [status, setStatus] = useState<SocketStatus>('connecting');
  const handlersRef = useRef<RunSocketHandlers>(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!runId) return;

    const socket: Socket = io(`${SOCKET_BASE}/runs`, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: { token: getToken() },
      // Explicit, bounded backoff so a dropped socket keeps trying to recover
      // rather than relying on undocumented defaults.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 8000,
    });

    setStatus('connecting');
    // Whether we've ever connected — a `connect` after this is a RECONNECT and
    // must backfill events missed while the socket was down.
    let hasConnected = false;

    const subscribe = () => {
      const reconnected = hasConnected;
      hasConnected = true;
      setStatus('connected');
      socket.emit('subscribe', { runId, orgId: getActiveOrgId() });
      if (reconnected) handlersRef.current.onReconnect?.();
    };

    socket.on('connect', subscribe);
    // socket.io's manager emits these during auto-reconnect; reflect them so the
    // UI can show a "reconnecting" (not "disconnected") state.
    socket.io.on('reconnect_attempt', () => setStatus('reconnecting'));
    socket.on('disconnect', (reason: string) => {
      setStatus('disconnected');
      // The gateway force-disconnects on an expired/invalid access token
      // (reason 'io server disconnect'), which socket.io-client never
      // auto-retries. Refresh silently and reconnect so an open run view
      // keeps streaming instead of going dark until the user reloads.
      if (reason === 'io server disconnect') {
        setStatus('reconnecting');
        refreshAccessToken().then((newToken) => {
          if (newToken) {
            socket.auth = { token: newToken };
            socket.connect();
          } else {
            setStatus('disconnected');
          }
        });
      }
    });
    socket.on('connect_error', () =>
      setStatus(hasConnected ? 'reconnecting' : 'disconnected'),
    );

    socket.on('run-event', (event: RunEvent) => {
      if (event.runId !== runId) return;
      const h = handlersRef.current;
      switch (event.type) {
        case RunEventType.Log:
          h.onLog?.(event.payload as LogLine, event.seq);
          break;
        case RunEventType.Stage:
          h.onStage?.(event.payload as StageDto, event.seq);
          break;
        case RunEventType.Status:
          h.onStatus?.(event.payload as RunSummaryDto, event.seq);
          break;
        case RunEventType.AgentEvent:
          h.onAgentEvent?.(event.payload as Record<string, unknown>, event.seq);
          break;
      }
    });

    return () => {
      socket.emit('unsubscribe', { runId });
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [runId]);

  return status;
}
